import { useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Send, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls =
  "w-full border border-white/15 bg-transparent px-4 py-3 text-sm text-white placeholder:text-[#616775] transition-colors focus:border-umber focus:outline-none";

export default function RequestCvModal({ open, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/cv-request`, { ...form, company: form.company || null, message: form.message || null });
      toast.success("CV request sent. I will be in touch shortly.");
      setForm({ name: "", email: "", company: "", message: "" });
      onClose();
    } catch {
      toast.error("Something went wrong sending your request. Please try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-labelledby="cv-modal-title"
          data-testid="cv-request-modal"
          className="fixed inset-0 z-[100] flex items-end justify-center bg-[#0B0C0E]/90 p-4 backdrop-blur-md sm:items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onClose}
        >
          <motion.form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg border border-white/10 bg-[#0B0C0E] p-6 sm:p-10"
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 24, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-start justify-between gap-6">
              <div>
                <p className="eyebrow mb-3">Request CV</p>
                <h2 id="cv-modal-title" className="text-2xl font-light tracking-tight sm:text-3xl">
                  A copy, sent to you directly.
                </h2>
              </div>
              <button type="button" data-testid="cv-modal-close" onClick={onClose} aria-label="Close" className="text-[#9EA3B0] transition-colors hover:text-white">
                <X size={20} />
              </button>
            </div>
            <div className="mt-8 grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="cv-name" className="eyebrow mb-2 block">Name</label>
                <input id="cv-name" data-testid="cv-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" />
              </div>
              <div>
                <label htmlFor="cv-email" className="eyebrow mb-2 block">Email</label>
                <input id="cv-email" data-testid="cv-email-input" required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@company.com" />
              </div>
            </div>
            <div className="mt-5">
              <label htmlFor="cv-company" className="eyebrow mb-2 block">Company or agency (optional)</label>
              <input id="cv-company" data-testid="cv-company-input" value={form.company} onChange={set("company")} className={inputCls} placeholder="Where you are hiring for" />
            </div>
            <div className="mt-5">
              <label htmlFor="cv-message" className="eyebrow mb-2 block">Note (optional)</label>
              <textarea id="cv-message" data-testid="cv-message-input" rows={3} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="The role, or anything I should know" />
            </div>
            <button
              type="submit"
              data-testid="cv-submit-button"
              disabled={sending}
              className="mt-8 inline-flex items-center gap-3 bg-white px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-[#0B0C0E] transition-all duration-300 hover:bg-umber disabled:opacity-50"
            >
              {sending ? "Sending…" : "Send request"} <Send size={14} />
            </button>
          </motion.form>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
